/**
 * Forgot-password screen — request a 6-digit reset code by email.
 *
 * PORTED from apps/cryb-native/app/(auth)/forgot-password.tsx (P3 AUTH group):
 *   - expo-router useRouter → react-navigation useNavigation.
 *
 * P1-AUTH REDESIGN: gradient/blobs → ParticleBackground + GlassSurface +
 * LogoWordmark, token-only styling. The flow now uses the 6-digit email OTP:
 * requestPasswordReset() issues the code, and we forward the email to
 * ResetPassword so the user enters the CODE (not a long token) there.
 */
import { useState } from 'react';
import {
  View, Text, TextInput, TouchableOpacity, StyleSheet, Alert,
  KeyboardAvoidingView, Platform, useWindowDimensions,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { Ionicons } from '../components/ui/Icon';
import ParticleBackground from '../components/ui/ParticleBackground';
import LogoWordmark from '../components/LogoWordmark';
import ScreenHeading from '../components/ui/ScreenHeading';
import Asset3D from '../components/ui/Asset3D';
import { GlassSurface, GlassButton } from '../components/glass';
import { requestPasswordReset } from '../services/api';
import type { AuthStackParamList } from '../navigation/types';
import { colors, spacing, radius, fonts, typographyScale, getLayout } from '../constants/theme';

type Nav = NativeStackNavigationProp<AuthStackParamList, 'ForgotPassword'>;

export default function ForgotPasswordScreen() {
  const navigation = useNavigation<Nav>();
  const insets = useSafeAreaInsets();
  const { width } = useWindowDimensions();
  const layout = getLayout(width);
  const cardWidth = layout !== 'phone'
    ? { maxWidth: layout === 'desktop' ? 460 : 420, alignSelf: 'center' as const, width: '100%' as const }
    : undefined;
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);

  // Basic email shape check. Guards against navigating to ResetPassword with an
  // empty/malformed email (that screen requires a non-empty email param).
  const isValidEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

  // Forward to the reset screen only with a valid email; otherwise block + show
  // an inline error instead of pushing an undefined/empty required param.
  function goToReset() {
    const trimmed = email.trim();
    if (!isValidEmail(trimmed)) {
      setEmailError('Enter a valid email to continue.');
      return;
    }
    setEmailError(null);
    navigation.navigate('ResetPassword', { email: trimmed });
  }

  async function handleReset() {
    const trimmed = email.trim();
    if (!isValidEmail(trimmed)) {
      setEmailError('Please enter a valid email address.');
      return;
    }
    setEmailError(null);
    setLoading(true);
    const result = await requestPasswordReset(trimmed);
    setLoading(false);

    // Anti-enumeration: the server always returns success. On a rate-limit (429)
    // surface the exact wait; otherwise show the neutral "check your inbox" card.
    if (result.success) {
      setSent(true);
    } else {
      const details = (result as any).details as Record<string, any> | undefined;
      const retryAfter = typeof details?.retryAfter === 'number' ? details.retryAfter : undefined;
      Alert.alert(
        'Please wait',
        retryAfter != null
          ? `Too many attempts. Try again in ${retryAfter < 60 ? `${retryAfter} seconds` : `${Math.ceil(retryAfter / 60)} minutes`}.`
          : (result.error || 'Failed to send reset code.'),
      );
    }
  }

  return (
    <View style={styles.root}>
      <ParticleBackground theme="light" />
      <KeyboardAvoidingView style={styles.flex} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
        <View
          style={[
            styles.center,
            { paddingTop: insets.top + spacing.xxl, paddingBottom: insets.bottom + spacing.xxl },
          ]}
        >
          <View style={[styles.stack, cardWidth]}>
            <View style={styles.brand}>
              <LogoWordmark size={34} />
            </View>

            {sent ? (
              <GlassSurface variant="card" radius={radius.xl} contentStyle={styles.cardBody} style={styles.card}>
                <Asset3D name="bell" size={84} style={styles.sentAsset} />
                <ScreenHeading title="Check your inbox" />
                <Text style={styles.subtitle}>
                  We sent a 6-digit code to{'\n'}
                  <Text style={styles.emphasis}>{email.trim()}</Text>
                  {'\n\n'}Enter it on the next screen to set a new password.
                </Text>
                <GlassButton
                  title="Enter Code"
                  onPress={() => navigation.navigate('ResetPassword', { email: email.trim() } as any)}
                  fullWidth
                  size="lg"
                />
                <TouchableOpacity
                  onPress={() => navigation.navigate('Login')}
                  activeOpacity={0.6}
                  style={styles.secondaryRow}
                  accessibilityRole="button"
                  accessibilityLabel="Back to sign in"
                >
                  <Text style={styles.secondaryText}>Back to Sign In</Text>
                </TouchableOpacity>
              </GlassSurface>
            ) : (
              <GlassSurface variant="card" radius={radius.xl} contentStyle={styles.cardBody} style={styles.card}>
                <ScreenHeading
                  title="Reset password"
                  subtitle="Enter your email and we'll send a 6-digit code."
                  style={styles.heading}
                />

                <TextInput
                  style={styles.input}
                  placeholder="your@email.com"
                  placeholderTextColor={colors.muted}
                  value={email}
                  onChangeText={(t) => { setEmail(t); if (emailError) setEmailError(null); }}
                  autoCapitalize="none"
                  keyboardType="email-address"
                  autoComplete="email"
                  returnKeyType="done"
                  onSubmitEditing={handleReset}
                />
                {!!emailError && <Text style={styles.errorText}>{emailError}</Text>}

                <GlassButton
                  title="Send Code"
                  onPress={handleReset}
                  loading={loading}
                  disabled={loading}
                  fullWidth
                  size="lg"
                />
                <TouchableOpacity
                  onPress={goToReset}
                  activeOpacity={0.6}
                  style={styles.secondaryRow}
                  accessibilityRole="button"
                  accessibilityLabel="Already have a code"
                >
                  <Text style={styles.secondaryText}>Already have a code?</Text>
                </TouchableOpacity>
              </GlassSurface>
            )}

            <TouchableOpacity
              onPress={() => navigation.navigate('Login')}
              style={styles.bottomRow}
              activeOpacity={0.6}
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              accessibilityRole="button"
              accessibilityLabel="Go to sign in"
            >
              <Text style={styles.bottomText}>
                Remember your password? <Text style={styles.bottomLink}>Sign in</Text>
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </KeyboardAvoidingView>
    </View>
  );
}

const styles = StyleSheet.create({
  root: { flex: 1, backgroundColor: colors.bg },
  flex: { flex: 1 },
  center: {
    flex: 1,
    justifyContent: 'center',
    paddingHorizontal: spacing.xxl,
  },
  stack: { width: '100%' },
  brand: {
    alignItems: 'center',
    marginBottom: spacing.xxl,
  },
  card: { marginBottom: spacing.xl },
  cardBody: { padding: spacing.xxl },
  heading: {
    marginBottom: spacing.xl,
  },
  sentAsset: {
    alignSelf: 'flex-start',
    marginBottom: spacing.md,
  },
  subtitle: {
    ...typographyScale.body,
    color: colors.ink2,
    marginTop: spacing.sm,
    marginBottom: spacing.xl,
  },
  emphasis: {
    color: colors.ink,
    ...fonts.semibold,
  },
  errorText: {
    ...typographyScale.caption,
    color: colors.error,
    marginTop: -spacing.sm,
    marginBottom: spacing.md,
    ...fonts.medium,
  },
  input: {
    height: 48,
    backgroundColor: colors.white,
    borderRadius: radius.md,
    paddingHorizontal: spacing.lg,
    ...typographyScale.body,
    color: colors.ink,
    borderWidth: 1,
    borderColor: colors.cardBorder,
    marginBottom: spacing.lg,
  },
  secondaryRow: {
    alignSelf: 'center',
    marginTop: spacing.lg,
  },
  secondaryText: {
    ...typographyScale.caption,
    color: colors.muted,
    ...fonts.medium,
  },
  bottomRow: {
    alignSelf: 'center',
  },
  bottomText: {
    ...typographyScale.body,
    color: colors.ink2,
  },
  bottomLink: {
    color: colors.primary,
    ...fonts.semibold,
  },
});
